import { format } from 'date-fns';
import { useEffect, useState } from 'react';

import { safeCreateStore, safeGet, safeSet } from '@canvas/idb-cache';
import { CustomerImpact, CapabilityTier } from '@canvas/operational-types';
import { captureExceptionForCapability } from '@canvas/sentry-react';

import { type FeatureName, messages } from './messages.ts';

export type AutoDismissalState = {
  times: number;
  daysSeen: number;
  lastSeenDayKey?: string;
  firstSeenAt?: number;
};

const STORE_DB_NAME = 'changeboarding-auto-dismissals';
const STORE_NAME = 'auto-dismissals';

const getStore = () => safeCreateStore(STORE_DB_NAME, STORE_NAME);

const getTodayKey = (): string => {
  return format(new Date(), 'yyyy-MM-dd');
};

const getDefaultState = (): AutoDismissalState => ({ times: 0, daysSeen: 0 });

export const readAutoDismissalState = async (
  feature: string,
): Promise<AutoDismissalState> => {
  try {
    const store = getStore();
    const state = await safeGet<AutoDismissalState>(feature, { customStore: store });
    return state ?? getDefaultState();
  } catch (e) {
    captureExceptionForCapability(
      e,
      {
        tags: {
          captureLocation: 'changeboarding-auto-dismissals-read',
          customerImpact: CustomerImpact.CUSTOMERS,
        },
      },
      [
        {
          capability: {
            importance: CapabilityTier.C3,
            kebabCaseName: 'changeboarding',
          },
        },
      ],
    );
    return getDefaultState();
  }
};

export const recordAutoDismissalExposure = async (
  feature: string,
): Promise<AutoDismissalState> => {
  const store = getStore();
  const existing = await readAutoDismissalState(feature);
  const config = messages[feature as keyof typeof messages]?.autoDismissal;

  // If limits are configured and already reached, avoid writing to indexeddb
  // every time.
  if (
    (config && config.times !== undefined && existing.times >= config.times) ||
    (config.days !== undefined && existing.daysSeen >= config.days)
  ) {
    return existing;
  }

  const todayKey = getTodayKey();
  const isNewDay = existing.lastSeenDayKey !== todayKey;

  const updatedTimes = (existing.times ?? 0) + 1;
  const updatedDays = (existing.daysSeen ?? 0) + (isNewDay ? 1 : 0);

  const updated: AutoDismissalState = {
    times: updatedTimes,
    daysSeen: updatedDays,
    lastSeenDayKey: todayKey,
    firstSeenAt: existing.firstSeenAt ?? Date.now(),
  };

  try {
    await safeSet(feature, updated, store);
  } catch (e) {
    captureExceptionForCapability(
      e,
      {
        tags: {
          captureLocation: 'changeboarding-auto-dismissals-write',
          customerImpact: CustomerImpact.CUSTOMERS,
        },
      },
      [
        {
          capability: {
            importance: CapabilityTier.C3,
            kebabCaseName: 'changeboarding',
          },
        },
      ],
    );
  }

  return updated;
};

export interface AutoDismissalCheckResult {
  shouldAutoDismiss: boolean;
  reason?: 'times' | 'days';
  state: AutoDismissalState;
}

export const checkShouldAutoDismiss = (
  feature: FeatureName,
  state: AutoDismissalState,
): AutoDismissalCheckResult => {
  const config = messages[feature]?.autoDismissal;
  if (!config) {
    return { shouldAutoDismiss: false, state };
  }

  const overTimes = typeof config.times === 'number' && state.times >= config.times;
  const overDays = typeof config.days === 'number' && state.daysSeen >= config.days;

  if (overTimes || overDays) {
    return {
      shouldAutoDismiss: true,
      reason: overTimes ? 'times' : 'days',
      state,
    };
  }

  return { shouldAutoDismiss: false, state };
};

export const useAutoDismissalCheck = (
  feature: FeatureName,
  skip = false,
): { loading: boolean; result: AutoDismissalCheckResult | null } => {
  const [loading, setLoading] = useState(!skip);
  const [result, setResult] = useState<AutoDismissalCheckResult | null>(null);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      setResult(null);
      return;
    }

    let cancelled = false;

    setLoading(true);

    const check = async () => {
      const state = await readAutoDismissalState(feature);
      if (!cancelled) {
        setResult(checkShouldAutoDismiss(feature, state));
        setLoading(false);
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [feature, skip]);

  return { loading, result };
};
